import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { MapPin } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AddressSearch, type AddressSearchResult } from "./address-search";

interface AddressFormFieldsProps {
  prefix?: string;
  showCard?: boolean;
  showRequiredIndicators?: boolean;
}

export function AddressFormFields({
  prefix = "address",
  showCard = true,
  showRequiredIndicators = true,
}: AddressFormFieldsProps) {
  const { t } = useTranslation(["masterdata", "common"]);
  const { control, setValue, trigger } = useFormContext();
  const fieldName = (name: string) => `${prefix}.${name}`;
  const getLabel = (translationKey: string, isRequired = false): string => {
    const label = t(translationKey);

    if (!isRequired || !showRequiredIndicators) {
      return label;
    }

    return `${label} *`;
  };

  const selectAddress = (address: AddressSearchResult) => {
    setValue(fieldName("street"), address.street);
    setValue(fieldName("houseNumber"), address.houseNumber);
    setValue(fieldName("postalCode"), address.postalCode);
    setValue(fieldName("city"), address.city);
    setValue(fieldName("country_code"), address.countryCode);
    setValue(fieldName("latitude"), address.latitude);
    setValue(fieldName("longitude"), address.longitude);

    void trigger(prefix);
  };

  const content = (
    <>
      <div className="mb-4">
        <AddressSearch onSelect={selectAddress} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          control={control}
          name={fieldName("recipientName")}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{getLabel("common:RecipientName")}</FormLabel>
              <FormControl>
                <Input placeholder={t("common:RecipientName")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={fieldName("company")}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{getLabel("common:Company")}</FormLabel>
              <FormControl>
                <Input placeholder={t("common:Company")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={fieldName("street")}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{getLabel("common:Street", true)}</FormLabel>
              <FormControl>
                <Input placeholder={t("common:Street")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={fieldName("houseNumber")}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{getLabel("common:HouseNumber", true)}</FormLabel>
              <FormControl>
                <Input placeholder={t("common:HouseNumber")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={fieldName("houseExtension")}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{getLabel("common:HouseExtension")}</FormLabel>
              <FormControl>
                <Input placeholder={t("common:HouseExtension")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={fieldName("postalCode")}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{getLabel("common:PostalCode", true)}</FormLabel>
              <FormControl>
                <Input placeholder={t("common:PostalCode")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={fieldName("city")}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{getLabel("common:City", true)}</FormLabel>
              <FormControl>
                <Input placeholder={t("common:City")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={fieldName("country_code")}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{getLabel("common:CountryCode", true)}</FormLabel>
              <FormControl>
                <Input placeholder={t("common:CountryCode")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={fieldName("latitude")}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{getLabel("common:Latitude", true)}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("common:Latitude")}
                  type="number"
                  step="any"
                  {...field}
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={fieldName("longitude")}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{getLabel("common:Longitude", true)}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("common:Longitude")}
                  type="number"
                  step="any"
                  {...field}
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  );

  if (!showCard) {
    return <div className="space-y-4">{content}</div>;
  }

  return (
    <Card className="shadow-none">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          {t("common:Address")}
        </CardTitle>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
